import { useNavigate, useLocation } from 'react-router-dom'
import styles from './Footer.module.css'

const Footer = () => {
  const currentYear = new Date().getFullYear()
  const navigate = useNavigate()
  const location = useLocation()

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault()

    // On the Solutions page, the contact form has its own section id
    const resolvedTargetId = location.pathname === '/solutions' && targetId === 'contact'
      ? 'solutions-contact'
      : targetId

    const isOnCorrectPage = location.pathname === '/solutions' || location.pathname === '/boxos'
    const homePath = location.pathname === '/solutions' ? '/solutions' : '/boxos'

    // If not on a page that has this section, navigate to home first
    if (!isOnCorrectPage) {
      navigate(homePath)
      setTimeout(() => {
        const element = document.getElementById(resolvedTargetId)
        if (element) {
          const offset = 80
          const elementPosition = element.getBoundingClientRect().top + window.scrollY
          const offsetPosition = elementPosition - offset
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
        }
      }, 100)
    } else {
      const element = document.getElementById(resolvedTargetId)
      if (element) {
        const offset = 80
        const elementPosition = element.getBoundingClientRect().top + window.scrollY
        const offsetPosition = elementPosition - offset
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
      }
    }
  }

  return (
    <footer className={styles.footer}>
      <div className="container">
        <div className={styles.content}>
          <div className={styles.brandSection}>
            <div className={styles.logo}>
              <img
                src="/boxos logo.png"
                alt="Box.OS - Rapid Results, Unbeatable"
                className={styles.logoImage}
              />
            </div>
          </div>

          <div className={styles.linksSection}>
            <div className={styles.linkColumn}>
              <h4 className={styles.columnTitle}>Navigation</h4>
              <ul className={styles.linkList}>
                <li><a href="#the-problem" className={styles.link} onClick={(e) => scrollToSection(e, 'the-problem')}>The Problem</a></li>
                <li><a href="#the-difference" className={styles.link} onClick={(e) => scrollToSection(e, 'the-difference')}>The Difference</a></li>
                <li><a href="#who-this-is-for" className={styles.link} onClick={(e) => scrollToSection(e, 'who-this-is-for')}>Who It's For</a></li>
              </ul>
            </div>

            <div className={styles.linkColumn}>
              <h4 className={styles.columnTitle}>Contact</h4>
              <ul className={styles.linkList}>
                <li><a href="#contact" className={styles.link} onClick={(e) => scrollToSection(e, 'contact')}>Get in Touch</a></li>
                <li><span className={styles.linkPrefix}>Send an email: </span><a href="mailto:contact@boxos.dev" className={styles.link}>contact@boxos.dev</a></li>
                {/* <li><a href="tel:+1234567890" className={styles.link}>+1 (234) 567-890</a></li> */}
              </ul>
            </div>

            <div className={styles.linkColumn}>
              <h4 className={styles.columnTitle}>Legal</h4>
              <ul className={styles.linkList}>
                <li><a href="#privacy" className={styles.link}>Privacy Policy</a></li>
                <li><a href="#terms" className={styles.link}>Terms of Service</a></li>
              </ul>
            </div>
          </div>
        </div>

        <div className={styles.divider} />

        <div className={styles.bottom}>
          <p className={styles.copyright}>
            © {currentYear} Box.os. All rights reserved.
          </p>

          <div className={styles.social}>
            <a href="#linkedin" className={styles.socialLink} aria-label="LinkedIn">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M4.5 3C3.67 3 3 3.67 3 4.5v11c0 .83.67 1.5 1.5 1.5h11c.83 0 1.5-.67 1.5-1.5v-11c0-.83-.67-1.5-1.5-1.5h-11zm2 2.5c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm-1 3h2v6h-2v-6zm3 0h2v.5c.5-.5 1.17-1 2.17-1 1.67 0 2.83 1.17 2.83 3v3.5h-2V12c0-1-.5-1.5-1.17-1.5-.83 0-1.17.5-1.17 1.5v3.5h-2v-6z"/>
              </svg>
            </a>
            <a href="#twitter" className={styles.socialLink} aria-label="Twitter">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M17.316 6.246c.008.162.011.326.011.488 0 4.99-3.797 10.742-10.74 10.742-2.133 0-4.116-.625-5.787-1.697a7.577 7.577 0 0 0 5.588-1.562 3.779 3.779 0 0 1-3.526-2.621 3.858 3.858 0 0 0 1.705-.065 3.779 3.779 0 0 1-3.028-3.703v-.048a3.766 3.766 0 0 0 1.71.473A3.775 3.775 0 0 1 2.08 3.713a10.716 10.716 0 0 0 7.781 3.945 3.813 3.813 0 0 1-.097-.863A3.776 3.776 0 0 1 16.291 5.66a7.742 7.742 0 0 0 2.397-.916 3.78 3.78 0 0 1-1.66 2.087c.763-.09 1.49-.293 2.168-.593a7.72 7.72 0 0 1-1.88 1.952z"/>
              </svg>
            </a>
            <a href="#github" className={styles.socialLink} aria-label="GitHub">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 2a8 8 0 0 0-2.53 15.59c.4.07.55-.17.55-.38l-.01-1.49c-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82a7.42 7.42 0 0 1 4 0c1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48l-.01 2.2c0 .21.15.46.55.38A8.01 8.01 0 0 0 10 2z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
